import { ensureMasterChainIdempotent, pruneOrphanStems } from '../graphNodes';
import { buildStem, connect, disconnectAll, MasterGraph, StemGraph } from './graphBuilder';
import type { EngineRefs } from './refs';

export interface GraphParams {
  masterVolume: number;
  masterFftOrder: number;
  masterHighpassHz: number;
  masterLowpassHz: number;
  q: number;
}

export interface GraphFlags {
  eqEnabled: boolean;
  eqHpfEnabled: boolean;
  eqLpfEnabled: boolean;
  freqResponseEnabled: boolean;
}

export async function wireGraph(
  ctx: AudioContext,
  tracks: { role: string; useRb?: boolean }[],
  refs: EngineRefs,
  params: GraphParams,
  flags: GraphFlags,
  getEffectiveGain: (role: string) => number,
) {
  // Ensure master graph exists
  ensureMasterChainIdempotent(
    ctx,
    {
      masterGainRef: refs.masterGainRef,
      gateGainRef: refs.gateGainRef,
      teeGainRef: refs.teeGainRef,
      masterAnalyserPreRef: refs.masterAnalyserPreRef,
      masterHighpassRef: refs.masterHighpassRef,
      masterFilterRef: refs.masterFilterRef,
      masterAnalyserRef: refs.masterAnalyserRef,
    },
    params,
    flags,
  );

  // Disconnect any previous stems first
  try { disconnectAll(refs.masterGraph, refs.stems); } catch {}

  // Build stems
  const stems: StemGraph[] = [];
  for (const t of tracks) {
    const el = refs.audioEls?.[t.role]; // expects refs.audioEls[role] = <audio> element
    if (!el) {
      //console.warn(`[wireGraph] no audio element for role=${t.role}`);
      continue;
    }
    try {
      const stem = await buildStem(ctx, t.role, el, {
        gain: getEffectiveGain(t.role),
        // Element path does not use RubberBand. Keep false to avoid any accidental instantiation hints.
        useRb: false,
      });
      stems.push(stem);
    } catch (err) {
      console.warn(`[wireGraph] Failed to build stem ${t.role}:`, err);
    }
  }

  refs.stems = stems;

  // Connect master + stems
  if (refs.masterGraph) {
    connect(ctx, refs.masterGraph as MasterGraph, stems, flags);
  }

  // Remove any orphaned stems that no longer exist
  const rolesNow = new Set(tracks.map(t => t.role));
  pruneOrphanStems(rolesNow, {
    sourcesRef: refs.sourcesRef,
    gainsRef: refs.gainsRef,
    analyzersRef: refs.analyzersRef,
    delaysRef: refs.delaysRef,
    connectedElsRef: refs.connectedElsRef,
    elsRef: refs.elsRef,
  });
}
